import React, { useEffect, useState, useRef } from 'react';
import { withStyles } from '@material-ui/core/styles';
import styles from '../styles';
import localStyles from './styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Grid, IconButton, Collapse } from '@material-ui/core';
import { withUserProfile } from '../../../../../../userProfile/userProfileContext';
import Select from '../../../../../common/StyledSelect';
import { compose } from 'recompose';
import clsx from 'clsx';
import { postData } from '../../../../../../api/componentAPIs';
import MESSAGES from '../../../../../../appMessages';
import { withSnackbar } from 'notistack';
import Events from '../../../../../../utilities/EventEmitter';
import {
  canAddDolly,
  getArrivedAtStopId,
  getNewHookedDolly,
  checkValidDollyNumber,
  getCurrentDollies,
  getInitialHookStateData,
} from '../utils';
import Action from './Action';

const DollyHook = ({
  userData,
  routeData,
  classes,
  isLoading,
  maxAllowedDolliesCount,
  showHideLoader,
  enqueueSnackbar,
  dollyListOptions,
  currentDriverRoutes,
  allowedDollyStatusesToHook,
}) => {
  let attachedTrailers = useRef(getCurrentDollies(currentDriverRoutes));
  const [hookData, setHookData] = useState(getInitialHookStateData());
  const [isExpanded, setIsExpand] = useState(false);

  useEffect(() => {
    attachedTrailers.current = getCurrentDollies(currentDriverRoutes);
  }, [routeData]);

  useEffect(() => {
    Events.on('dollyHooked', dollyHookedStatus);
    return () => {
      Events.off('dollyHooked', dollyHookedStatus);
    };
  }, []);

  const dollyHookedStatus = (data) => {
    data.success ? onDollyHookSucceed() : onDollyHookFailed();
  };

  const onDollyHookSucceed = () => {
    enqueueSnackbar(MESSAGES.DOLLY_HOOKED.SUCCESS, {
      variant: 'success'
    });
    showHideLoader(false);
    clearSelectedDollies();
  };

  const clearSelectedDollies = () => {
    setHookData(getInitialHookStateData());
  }

  const onDollyHookFailed = (statusCode) => {
    showHideLoader(false);
    if (statusCode === 403) {
      return;
    }
    enqueueSnackbar(MESSAGES.DOLLY_HOOKED.FAILURE, {
      variant: 'error'
    });
  };

  const replaceDollyError = (dollyData) => {
    setHookData(
      dollyData.map((dolly) => ({
        ...dolly,
        hookDollyError: checkValidDollyNumber({
          hookDollies: dollyData,
          dollyId: dolly.hookDolly.dollyId,
          dollyStatus: dolly.hookDolly.dollyStatus,
          allowedDollyStatusesToHook
        })
      }))
    );
  }

  const handleDollyNumberChange = (dollyIndex) => (value) => {
    const dollyNumber = value?.value;
    const { dollyScac, dollyId, dollyStatus } = value ?? {};
    const newHookData = hookData;
    newHookData[dollyIndex] = {
      hookDolly: {
        ...newHookData[dollyIndex].hookDolly,
        dollyNumber,
        dollyScac,
        dollyId,
        dollyStatus,
      }
    };
    replaceDollyError(newHookData);
  };

  const addNewDolly = () => {
    setHookData([
      ...hookData,
      {
        hookDolly: getNewHookedDolly(),
        hookDollyError: null
      }
    ]);
  };

  const removeHookDolly = () => {
    const newDollyData = hookData.toSpliced(hookData.length - 1, 1);
    replaceDollyError(newDollyData);
  };

  const getDollyNumberErrors = () => {
    return hookData.map((dolly) => ({
      ...dolly,
      hookDollyError: checkValidDollyNumber({
        hookDollies: hookData,
        dollyId: dolly.hookDolly.dollyId,
        dollyStatus: dolly.hookDolly.dollyStatus,
        allowedDollyStatusesToHook
      })
    }));
  };

  const getPayloadData = () => {
    const requestData = {
      terminalCode: userData.userProfile.terminal.code,
      driverId: routeData.driverId,
      dispatchId: routeData.dispatchId,
      equipments: hookData.reduce((hookedDollies, dollyDetails) => {
        if (!dollyDetails.hookDolly.dollyId) {
          return hookedDollies;
        }
        return [
          ...hookedDollies,
          {
            dollyNumber: dollyDetails.hookDolly.dollyNumber ?? null,
            dollyScac: dollyDetails.hookDolly.dollyScac ?? null,
            trailerNumber: null, //backend using same model for trailer/dolly hook so they need these fields as well
            trailerScac: null
          }
        ];
      }, []),
      arrivedStopId: getArrivedAtStopId(routeData)
    };
    return requestData;
  };

  const onHookDolly = () => {
    const hookDataWithError = getDollyNumberErrors();
    const hasValidInputs = hookDataWithError.some((hookData) => hookData.hookDollyError);
    if (hasValidInputs) {
      setHookData(hookDataWithError);
      return;
    }
    const requestData = getPayloadData();
    showHideLoader(true);
    postData('TrailerHook', requestData)
      .then((response) => {
        if (response?.status !== 204) {
          onDollyHookFailed(response.status);
        }
      })
      .catch(() => {
        onDollyHookFailed();
      });
  };

  const isAddDollyDisabled = canAddDolly({
    attachedDolliesCount: attachedTrailers.current?.length,
    hookedDolliesCount: hookData.length,
    maxAllowedDolliesCount
  });
  const isLastEntry = hookData.length === 1;

  return (
    <Grid container className={classes.containerStyle}>
      <Grid item xs={12}>
        <div className={classes.headerSection}>
          <div className={classes.dollySectionTitle}>Dolly Hook</div>
          <IconButton
            className={clsx(classes.expand, {
              [classes.expandOpen]: isExpanded
            })}
            onClick={() => setIsExpand(!isExpanded)}
            aria-expanded={isExpanded}
            aria-label="dolly expand">
            <ExpandMoreIcon className={classes.expandIcon} />
          </IconButton>
        </div>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {hookData.map((dolly, hookDollyIndex) => (
            <div className={classes.searchRow} key={`hookDolly-${hookDollyIndex}`}>
              <Select
                value={dolly.hookDolly.dollyNumber}
                data={dollyListOptions}
                onChange={handleDollyNumberChange(hookDollyIndex)}
                rootClass={classes.selectRootHook}
                textFieldProps={{
                  id: 'hookDollyList',
                  label: 'Dolly Number',
                  variant: 'outlined',
                  helperText: dolly.hookDollyError
                }}
                error={Boolean(dolly.hookDollyError)}
              />
            </div>
          ))}
          <Action
            onAdd={addNewDolly}
            onCancel={removeHookDolly}
            onSubmit={onHookDolly}
            disabledCancel={isLoading || isLastEntry}
            disabledAdd={isLoading || isAddDollyDisabled}
            disableSubmit={isLoading}
          />
        </Collapse>
      </Grid>
    </Grid>
  );
};

export default compose(
  withSnackbar,
  withUserProfile,
  withStyles(
    (theme) => ({
      ...styles(theme),
      ...localStyles(theme)
    }),
    { withTheme: true }
  )
)(DollyHook);
